import { useContext, useEffect, useState } from 'react';
import BZAPIService from '../../../services/BZAPIService';
import { TenantSettingsContext } from '../../../context/TenantSettingsContext';
import { featureIsReadOnlyCheck } from '../../../helpers/rbac';
import { scanCapable } from './productDropdownGroups';

/**
 * May this user start a Plan project for this vendor from Prevent? (STRY0038414)
 *
 * Not just "the same check Plan itself applies" -- it must actually BE that
 * check (canModifyProjects in PlanPage.js), which refuses to trust a
 * positive `bsaEnabled` reading until tenant settings have loaded, because
 * the context defaults `bsaEnabled` to `true` optimistically. Without the
 * `isLoaded` guard here, a Plan-disabled tenant could briefly see this gate
 * as true, firing a live CMDB read and caching a count for the whole session
 * (finding 4). Plus the scan-capability gate: "Create Plan project" kicks off
 * a live CMDB scan, so it must not be offered to a read-only user, on a
 * Plan-disabled tenant, or for a vendor with no inventory adapter
 * (STRY0038603).
 *
 * Lifted out of ManagedProductListing so all three Prevent surfaces share one
 * copy of the rule instead of three. Both error defaults are deliberate and
 * unchanged: read-only on a permissions error, no scan on a vendor-list error.
 */
export function usePlanProjectGate(vendorId) {
  const { bsaEnabled, isLoaded } = useContext(TenantSettingsContext);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [inventoryVendors, setInventoryVendors] = useState(null);

  useEffect(() => {
    let cancelled = false;
    BZAPIService.getMyUserPermissions()
      .then(permissions => {
        if (!cancelled) setIsReadOnly(featureIsReadOnlyCheck('bugscrubadvisor', permissions));
      })
      .catch(() => {
        if (!cancelled) setIsReadOnly(true); // default to read-only on error
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    BZAPIService.bsaInvVendors()
      .then(list => {
        if (!cancelled) setInventoryVendors(list);
      })
      .catch(() => {
        if (!cancelled) setInventoryVendors([]); // unavailable -> offer no scan
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return isLoaded && bsaEnabled && !isReadOnly && scanCapable(vendorId, inventoryVendors);
}
